import React, {useState} from 'react';
import PropTypes from 'prop-types';
import useDidMountEffect from "utils/useDidMountEffect";
import apiUser from "apiUser";
import LoadingSpinner from "../../../../LoadingSpinner/LoadingSpinner";
import minutesToHours from "utils/minutesToHours";
import Icon from "componentsReusable/Icon";
import {xor} from "lodash";
import useAfterUpdateEffect from "utils/useAfterUpdateEffect";
import {Card, CardBody, CardHeader} from "reactstrap";
import {useTecRmi} from "../../../../../contexts/TecRmiContext";
import WorkTimeMultiplier from "../../../WorkTimeMultiplier";
import TreeMenuNew, {DISPLAY_TYPE_TREE} from "../../../../../componentsReusable/TreeMenuNew/TreeMenuNew";
import {useTranslation} from "react-i18next";

const WORK_STEP_TYPE_MAIN = 'main'
const WORK_STEP_TYPE_ADDITIONAL = 'additional'

const MaintenanceWorks = props => {
  const { t } = useTranslation('tec_rmi');
  const {getApiPrimaryParams, workTimeMultiplier} = useTecRmi()
  const [loading, setLoading] = useState(true)
  const [works, setWorks] = useState([])
  const [mainWorkId, setMainWorkId] = useState(props.data.mainWorkId)
  const [additionalWorkIds, setAdditionalWorkIds] = useState(props.data.additionalWorkIds)

  useAfterUpdateEffect(() => {
    if(!mainWorkId){
      props.serviceProposal.forEach(serviceProposal => {
        works.Services.forEach(service => {
          if(service.WorkSteps.find(workStep => workStep.WorkId === serviceProposal.WorkId)){
            setMainWorkId(serviceProposal.WorkId)
          }
        })
      })
    }

    if(additionalWorkIds.length === 0){
      const _additionalWorkIds = []
      props.serviceProposal.forEach(serviceProposal => {
        works.AdditionalWorks.forEach(additionalWork => {
          if(additionalWork.WorkSteps.find(workStep => workStep.WorkId === serviceProposal.WorkId)){
            _additionalWorkIds.push(serviceProposal.WorkId)
          }
        })
      })

      setAdditionalWorkIds(_additionalWorkIds)
    }
  }, [works])

  useAfterUpdateEffect(() => {
    setWorks(works => ({
      ...works,
      AdditionalWorks: works.AdditionalWorks.map(additionalWork => {
        additionalWork.WorkSteps = additionalWork.WorkSteps.map(workStep => {
          workStep.WorkTime = workStep.WorkTimeDefault * workTimeMultiplier

          return workStep
        })

        return additionalWork
      }),
      Services: works.Services.map(service => {
        service.WorkSteps = service.WorkSteps.map(workStep => {
          workStep.WorkTime = workStep.WorkTimeDefault * workTimeMultiplier

          return workStep
        })

        return service
      }),
    }))
  }, [workTimeMultiplier])

  useDidMountEffect(() => {
    setLoading(true)

    apiUser.tecRmi.maintenance.works({
      ...getApiPrimaryParams(),
      kindOfWorkTime: 0,
      bodyQualColId: props.bodyQualColId,
    })
      .then(res => setWorks({
        ...res.data,
        AdditionalWorks: res.data.AdditionalWorks.map(additionalWork => {
          additionalWork.WorkSteps = additionalWork.WorkSteps.map(workStep => {
            workStep.WorkTimeDefault = workStep.WorkTime
            workStep.WorkTime = workStep.WorkTime * workTimeMultiplier

            return workStep
          })

          return additionalWork
        }),
        Services: res.data.Services.map(service => {
          service.WorkSteps = service.WorkSteps.map(workStep => {
            workStep.WorkTimeDefault = workStep.WorkTime
            workStep.WorkTime = workStep.WorkTime * workTimeMultiplier

            return workStep
          })

          return service
        }),
      }))
      .finally(() => setLoading(false))
  })

  useAfterUpdateEffect(() => {
    props.onDataChange({
      works,
      mainWorkId,
      additionalWorkIds
    })
  }, [works, mainWorkId, additionalWorkIds])

  const workStepLabel = (workStep, type) => {
    let icon
    switch (type) {
      case WORK_STEP_TYPE_MAIN: {
        icon = mainWorkId === workStep.WorkId
          ? <Icon className="far fa-dot-circle text-success" style={{ fontSize: '1.4em' }} tooltip={t('service_proposal.stage_2.unselect')} />
          : <Icon className="far fa-circle" style={{ fontSize: '1.4em' }} tooltip={t('service_proposal.stage_2.select')} />
        break
      }
      case WORK_STEP_TYPE_ADDITIONAL: {
        icon = additionalWorkIds.includes(workStep.WorkId)
          ? <Icon className="far fa-check-square text-success" style={{ fontSize: '1.4em' }} tooltip={t('service_proposal.stage_2.unselect')} />
          : <Icon className="far fa-square" style={{ fontSize: '1.4em' }} tooltip={t('service_proposal.stage_2.select')} />
        break
      }

    }
    return (
      <span>
        {icon}
        <span className="ml-2 text-secondary">{minutesToHours(workStep.WorkTime, false, 2)}</span>
        <span className="ml-2">{[workStep.ItemMpText, workStep.KorText, (workStep.QualColId > 0 ? workStep.QualColText : '')].join(' ')}</span>
      </span>
    )
  }

  const getItemsMain = () => works.Services.map(service => {
    let obj = {
      key: service.ItemMpId.toString(),
      id: service.ItemMpId,
    }

    if(service.WorkSteps.length === 1){
      const workStep = service.WorkSteps[0]
      obj._type = WORK_STEP_TYPE_MAIN
      obj._data = workStep
      obj.label = workStepLabel(workStep, WORK_STEP_TYPE_MAIN)
      obj.onClick = item => {
        setMainWorkId(item._data.WorkId)
      }
    }else{
      obj.label = <span>
                  <Icon className="fas fa-info-circle px-1" tooltip={t('service_proposal.stage_2.choose_available')} />
                  <span className="ml-2">{[service.ItemMpText, service.KorText].join(' ')}</span>
                </span>
      obj.items = service.WorkSteps.map(workStep => ({
        _type: WORK_STEP_TYPE_MAIN,
        _data: workStep,
        key: 'w' + workStep.WorkId,
        id: workStep.WorkId,
        label: workStepLabel(workStep, WORK_STEP_TYPE_MAIN),
        onClick: item => {
          setMainWorkId(item._data.WorkId)
        }
      }))
    }

    return obj
  })

  const getItemsAdditional = () => works.AdditionalWorks.map(additionalWork => {
    let obj = {
      key: additionalWork.ItemMpId,
      id: additionalWork.ItemMpId,
    }

    if(additionalWork.WorkSteps.length === 1){
      const workStep = additionalWork.WorkSteps[0]
      obj._type = WORK_STEP_TYPE_ADDITIONAL
      obj._data = workStep
      obj.label = <span>
                  {
                    additionalWorkIds.includes(workStep.WorkId)
                      ? <Icon className="far fa-check-square text-success" style={{ fontSize: '1.4em' }} tooltip={t('service_proposal.stage_2.unselect')} />
                      : <Icon className="far fa-square" style={{ fontSize: '1.4em' }} tooltip={t('service_proposal.stage_2.select')} />
                  }
        <span className="ml-2 text-secondary">{minutesToHours(workStep.WorkTime, false, 2)}</span>
                  <span className="ml-2">{[additionalWork.ItemMpText, additionalWork.KorText, (additionalWork.AddText ? `[${additionalWork.AddText}]` : '')].join(' ')}</span>
                </span>
      obj.onClick = item => setAdditionalWorkIds(xor(additionalWorkIds, [item._data.WorkId]))
    }else{
      obj.label = <span>
                  <Icon className="fas fa-info-circle px-1" tooltip={t('service_proposal.stage_2.choose_available')} />
                  <span className="ml-2">{[additionalWork.ItemMpText, additionalWork.KorText, (additionalWork.AddText ? `[${additionalWork.AddText}]` : '')].join(' ')}</span>
                </span>
      obj.items = additionalWork.WorkSteps.map(workStep => ({
          _type: WORK_STEP_TYPE_ADDITIONAL,
          _data: workStep,
          key: 'w' + workStep.WorkId,
          id: workStep.WorkId,
          label: workStepLabel(workStep, WORK_STEP_TYPE_ADDITIONAL),
          onClick: item => setAdditionalWorkIds(xor(additionalWorkIds, [item._data.WorkId])),
        }
      ))
    }

    return obj
  })

  if(loading){
    return <LoadingSpinner />
  }

  return (
    <div>
      <div className="d-flex justify-content-end mb-2">
        <WorkTimeMultiplier />
      </div>
      <Card>
        <CardHeader>{t('service_proposal.stage_2.main_works_header')}</CardHeader>
        <CardBody>
          <TreeMenuNew
            actives={[
              [works.Services.find(service => service.WorkSteps.find(workStep => workStep.WorkId === props.data.mainWorkId))?.ItemMpId].filter(v => !!v)
            ]}
            items={getItemsMain()}
            displayType={DISPLAY_TYPE_TREE}
            isMulti
          />
        </CardBody>
      </Card>
      <Card className="mt-3">
        <CardHeader>{t('service_proposal.stage_2.additional_works_header')}</CardHeader>
        <CardBody>
          <TreeMenuNew
            actives={
              props.data.additionalWorkIds
                .map(workId => ([
                  works.AdditionalWorks.find(additionalWork => additionalWork.WorkSteps.find(workStep => workStep.WorkId === workId))?.ItemMpId
                ].filter(v => !!v)))
            }
            items={getItemsAdditional()}
            displayType={DISPLAY_TYPE_TREE}
            isMulti
          />
        </CardBody>
      </Card>
    </div>
  );
};

MaintenanceWorks.propTypes = {
  onDataChange: PropTypes.func.isRequired,
  bodyQualColId: PropTypes.number.isRequired,
};

export default MaintenanceWorks;